import { Fragment } from 'react'

import { t } from 'i18next'

import {
  TFailPage,
  TLTPPaymentScreen,
  TPageReceiptData,
  TPreLTPPage,
  TSelectLTPPage,
} from '@/config/types'
import { SelectLTPBlockType } from '@/enums/ltp'
import { PageName } from '@/enums/pageName'
import { PaymentMethodService } from '@/enums/paymentMethodServices'
import { formatCurrency } from '@/utils/currencyUtils'
import { downloadImage } from '@/utils/imageUtils'
import { checkSelectContentBlocks } from '@/utils/pageConfigUtils'
import { getPath } from '@/utils/urlUtils'

import { ListWithIcons, TListWithIconsProps } from '@/components/listWithIcons'
import { TextHTML } from '@/components/typography'
import { FailRectRounded } from '@/shared/icons/FailRectRounded'
import { LogoSenses } from '@/shared/icons/LogoSenses'
import { RoundCheckbox3 } from '@/shared/icons/RoundCheckbox3'
import ltpKetoGuide from '@/shared/organic-fit/images/ltp/keto-guide.png'
import ltpUnleashYourPotential from '@/shared/organic-fit/images/ltp/unleash-your-potential.png'
import ltpVeganGuide from '@/shared/organic-fit/images/ltp/vegan-guide.png'

import { contentDetails } from '../contentDetails'
import { plans } from '../planDetails'

const ltpUnleashYourPotentialImg = downloadImage(ltpUnleashYourPotential)
const ltpKetoGuideImg = downloadImage(ltpKetoGuide)
const ltpVeganGuideImg = downloadImage(ltpVeganGuide)

export const preLTPPage: TPreLTPPage = {
  pageTitle: t('preLTPPage.pageTitle'),
  contentDetails,
  header: t('preLTPPage.header'),
  description: t('preLTPPage.description'),
  iconNode: <RoundCheckbox3 circleColor="#FF7365" checkColor="#FF7365" />,
}

const paymentModalId = 'payment-modal-window'
export const ltpSelectPage: TSelectLTPPage = {
  pageTitle: t('ltpSelectPage.tabTitle'),
  fastPaymentSuccessUrl: getPath(PageName.paymentSuccess),
  skipUrl: getPath(PageName.paymentSuccess),
  paymentDisclaimer: t('ltpSelectPage.paymentDisclaimer'),
  contentDetails,
  popupAppLogo: <LogoSenses />,
  blocks: [
    () => {
      return (
        <Fragment key="title">
          <div className="page-header">{t('ltpSelectPage.pageHeader')}</div>
          <TextHTML className="page-title" title={t('ltpSelectPage.title')} />
        </Fragment>
      )
    },
    {
      key: 'ltp-info',
      blockType: SelectLTPBlockType.singleLTPInfo,
    },
    () => {
      return (
        <div className="content-description-title" key="10 books">
          <TextHTML key="description" title={t('ltpSelectPage.contentDescription')} />
        </div>
      )
    },
    () => {
      const props: TListWithIconsProps = {
        className: 'content-description-container',
        items: [
          {
            id: 1,
            iconNode: ltpKetoGuideImg,
            title: 'Keto Guide',
            description: 'Ideal for beginners and seasoned keto followers',
          },
          {
            id: 2,
            iconNode: ltpUnleashYourPotentialImg,
            title: 'Unleash Your Potential',
            description: 'Maximize performance with the right gear, shoes, and progress tracking',
          },
          {
            id: 3,
            iconNode: ltpVeganGuideImg,
            title: 'Vegan Guide',
            description: 'Thrive on a vegan diet, lose weight, and enjoy nutritious, tasty meals',
          },
        ],
      }
      return (
        <div className="content-description-section" key="what-you-get">
          <ListWithIcons {...props} />
        </div>
      )
    },
    {
      key: 'payment-modal-window',
      blockType: SelectLTPBlockType.paymentModalWindow,
      modalId: paymentModalId,
    },
    {
      key: 'add-button',
      blockType: SelectLTPBlockType.fastPaymentButton,
      modalId: paymentModalId,
      buttonName: t('ltpSelectPage.buttonName'),
      skipButtonName: t('ltpSelectPage.skipButtonName'),
    },
  ],
}

checkSelectContentBlocks(ltpSelectPage.blocks)

function getReceiptData(): TPageReceiptData {
  const receiptData: TPageReceiptData = {}
  for (const i in contentDetails) {
    const contentPrice = contentDetails[i]
    const amount = formatCurrency(contentPrice.price, contentPrice.currency, 2, false)

    const discountAmount =
      contentPrice.currencySign + (+contentPrice.crossedPrice - contentPrice.price).toFixed(2)
    receiptData[contentPrice.contentId] = {
      priceInfo: [
        {
          textPlan: contentPrice.name,
          textPrice: amount,
          extraClassName: 'header',
        },
        {
          textPlan: t('ltpPaymentScreenPage.receiptData.discountRow').replace(
            '{discountPercentage}',
            contentPrice.discountPercentage || ''
          ),
          textPrice: discountAmount,
          extraClassName: 'discount',
          priceExtraClassName: 'highlight',
        },
      ],
      totalPrice: {
        textPlan: t('ltpPaymentScreenPage.receiptData.totalText'),
        textPrice: amount,
        weekTextPrice: contentPrice.discountPercentage
          ? t('ltpPaymentScreenPage.receiptData.discountSavedAmount')
              .replace('{amount}', discountAmount)
              .replace('{percentage}', contentPrice.discountPercentage)
          : t('ltpPaymentScreenPage.receiptData.discountSavedAmountNoPercentage').replace(
              '{amount}',
              discountAmount
            ),
      },
    }
  }
  return receiptData
}

export const ltpPaymentScreenPage: TLTPPaymentScreen = {
  pageTitle: t('ltpPaymentScreenPage.pageTitle'),
  discount: t('ltpPaymentScreenPage.discount'),
  receiptData: getReceiptData(),
  stripeFormConfig: {
    paymentButtonText: t('ltpPaymentScreenPage.paymentButtonText'),
    paymentMethodSwitcherIcons: {
      order: [
        PaymentMethodService.VISA,
        PaymentMethodService.AMEX_TRANSPARENT,
        PaymentMethodService.MASTERCARD,
        PaymentMethodService.MAESTRO,
      ],
    },
  },
  walletButtonConfig: {
    underForm: true,
    showDelimiter: false,
  },
  showCompanyContacts: true,
  showGuaranteesLabel: true,
  disclaimerTemplate: t('ltpPaymentScreenPage.disclaimerTemplate'),
}

export const ltpPaymentFailPage: TFailPage = {
  paymentTitle: t('ltpPaymentFailPage.paymentTitle'),
  pageTitle: t('ltpPaymentFailPage.pageTitle'),
  message: t('ltpPaymentFailPage.message'),
  buttonName: t('ltpPaymentFailPage.buttonName'),
  iconNode: <FailRectRounded strokeColor="red" />,
  plansDetails: plans,
}
